import { message } from "antd";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Loader } from "../../../../components";
import {
  Modal,
  ModalButton,
  ModalCancelButton,
  ModalFooter,
} from "../../../../components/common/Modal";
import { COLORS } from "../../../../constants";
import { getErrorMessage } from "../../../../utils/errors";

const HELP_CENTER_DUPLICATE_URL =
  "https://help.coefficient.io/hc/en-us/articles/49670880380059-Web-Dashboards";

interface DuplicateDashboardModalProps {
  visible: boolean;
  /** Source dashboard name — seeds the copy title. */
  sourceName: string;
  /** Link to the source dashboard — referenced in the "different data" copy-paste prompt. */
  sourceUrl: string;
  /**
   * Perform the same-data duplicate. Resolve on success; REJECT on failure so the modal can show
   * the reason inline (the caller should not swallow the error or toast it). The caller handles
   * navigation/close on success.
   */
  onDuplicateSameData: (title: string) => Promise<void>;
  onCancel: () => void;
}

type Mode = "same" | "different";

/** The ready-to-paste builder prompt for the "different data" flow. */
const buildRebuildPrompt = (sourceUrl: string): string =>
  `Rebuild the "${sourceUrl}" dashboard using this spreadsheet's data.`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: ${COLORS.black65};
`;

const TitleInput = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 2px solid ${COLORS.black15};
  border-radius: 4px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${COLORS.coeblue4};
  }
`;

const HelperText = styled.p`
  margin: 10px 0 0;
  font-size: 12px;
  color: ${COLORS.black45};
`;

const ModeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const ModeOption = styled.label<{ selected: boolean }>`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border: 2px solid ${props => (props.selected ? COLORS.coeblue4 : COLORS.black15)};
  border-radius: 6px;
  cursor: pointer;
`;

const ModeText = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModeTitle = styled.span`
  font-size: 14px;
  color: ${COLORS.black85};
`;

const ModeDesc = styled.span`
  font-size: 12px;
  color: ${COLORS.black45};
`;

const PromptBox = styled.textarea`
  width: 100%;
  min-height: 84px;
  resize: vertical;
  padding: 8px 10px;
  border: 2px solid ${COLORS.black15};
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.4;
  color: ${COLORS.black85};
  background: ${COLORS.black2};
  outline: none;
  word-break: break-word;
`;

const LearnMore = styled.a`
  font-size: 12px;
  color: ${COLORS.coeblue4};
`;

const ErrorBanner = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid ${COLORS.red};
  border-radius: 6px;
  background: ${COLORS.redBg};
  font-size: 13px;
  line-height: 1.4;
  color: ${COLORS.red};
`;

/**
 * Duplicate a dashboard. Two paths:
 *  - Same data: instant exact copy (backend-only, reuses the source's data).
 *  - Different data: no AI here — show a ready-to-copy prompt the user pastes into the dashboard
 *    builder on the other sheet, plus a Help Center link. "Copy prompt" is the footer action.
 */
export const DuplicateDashboardModal: React.FC<DuplicateDashboardModalProps> = ({
  visible,
  sourceName,
  sourceUrl,
  onDuplicateSameData,
  onCancel,
}) => {
  const [mode, setMode] = useState<Mode>("same");
  const [titleValue, setTitleValue] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setMode("same");
      setTitleValue(sourceName ? `${sourceName} (Copy)` : "");
      setSubmitting(false);
      setErrorMessage(null);
    }
  }, [visible, sourceName]);

  const rebuildPrompt = buildRebuildPrompt(sourceUrl);
  const confirmDisabled = mode === "same" && (!titleValue.trim() || submitting);

  // Own the submit + error state so the failure reason shows INLINE (a top toast is easy to miss).
  // The caller closes/navigates on success; on failure it rejects and we surface the reason here.
  const confirm = async () => {
    if (confirmDisabled || mode !== "same") {
      return;
    }
    const fallback = "Couldn't duplicate this dashboard. Please try again.";
    setErrorMessage(null);
    setSubmitting(true);
    try {
      await onDuplicateSameData(titleValue.trim());
    } catch (err) {
      setErrorMessage(getErrorMessage(err, fallback) || fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(rebuildPrompt);
      message.success("Prompt copied");
    } catch {
      message.error("Couldn't copy — select the text and copy manually.");
    }
  };

  return (
    <Modal
      title="Duplicate dashboard"
      visible={visible}
      onClose={onCancel}
      width={520}
      hideCloseButton
    >
      <ModeList>
        <ModeOption selected={mode === "same"}>
          <input
            type="radio"
            name="duplicate-mode"
            checked={mode === "same"}
            onChange={() => {
              setMode("same");
              setErrorMessage(null);
            }}
          />
          <ModeText>
            <ModeTitle>Same data</ModeTitle>
            <ModeDesc>An exact copy that uses the same data. Opens ready to view.</ModeDesc>
          </ModeText>
        </ModeOption>
        <ModeOption selected={mode === "different"}>
          <input
            type="radio"
            name="duplicate-mode"
            checked={mode === "different"}
            onChange={() => {
              setMode("different");
              setErrorMessage(null);
            }}
          />
          <ModeText>
            <ModeTitle>Different data</ModeTitle>
            <ModeDesc>Rebuild this dashboard on another sheet using the AI builder.</ModeDesc>
          </ModeText>
        </ModeOption>
      </ModeList>

      {mode === "same" ? (
        <>
          <FieldLabel>Name</FieldLabel>
          <TitleInput
            value={titleValue}
            onChange={e => {
              setTitleValue(e.target.value);
              if (errorMessage) {
                setErrorMessage(null);
              }
            }}
            onKeyDown={e => {
              if (e.key === "Enter" && !confirmDisabled) {
                confirm();
              }
            }}
            autoFocus
          />
          {errorMessage ? (
            <ErrorBanner role="alert">{errorMessage}</ErrorBanner>
          ) : (
            <HelperText>
              The copy uses the same data as the original and opens ready to view.
            </HelperText>
          )}
        </>
      ) : (
        <>
          <FieldLabel>Prompt for the AI builder</FieldLabel>
          <PromptBox readOnly value={rebuildPrompt} onFocus={e => e.target.select()} />
          <HelperText>
            Open the dashboard builder on the other sheet and paste this to rebuild it there.{" "}
            <LearnMore href={HELP_CENTER_DUPLICATE_URL} target="_blank" rel="noreferrer">
              Learn more
            </LearnMore>
          </HelperText>
        </>
      )}

      <ModalFooter>
        <ModalCancelButton onClick={onCancel}>Cancel</ModalCancelButton>
        {mode === "same" ? (
          <ModalButton onClick={confirm} disabled={confirmDisabled}>
            {submitting ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Loader color="white" size="micro" />
                Duplicating…
              </span>
            ) : (
              "Duplicate"
            )}
          </ModalButton>
        ) : (
          <ModalButton onClick={copyPrompt}>Copy prompt</ModalButton>
        )}
      </ModalFooter>
    </Modal>
  );
};
